// Serenity Star API Client for DFS Collections
import { config } from '../config/environment';

const API_BASE = `${config.serenityBaseUrl}/api/v2`;

interface QueryResult {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  executionTimeMs: number;
}

// Response from creating/getting a conversation
interface ConversationResponse {
  chatId: string;
  content: string;
  conversationStarters: string[];
  version: number;
  useVision: boolean;
}

// Response from executing an agent (both activity and chat messages)
interface ExecuteResponse {
  content: string;
  jsonContent?: unknown;
  instanceId: string;
  userMessageId?: string;
  agentMessageId?: string;
  completionUsage?: {
    completionTokens: number;
    promptTokens: number;
    totalTokens: number;
  };
}

// Legacy interface for backward compatibility
interface AgentResponse {
  response: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
}

class SerenityClient {
  private apiKey: string;
  private datasetId: string;

  constructor() {
    this.apiKey = config.serenityApiKey;
    this.datasetId = config.datasetId;
  }

  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': this.apiKey,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    return response.json();
  }

  // Dataset queries
  async query(sql: string): Promise<QueryResult> {
    return this.fetch<QueryResult>(`/Dataset/${this.datasetId}/query`, {
      method: 'POST',
      body: JSON.stringify({
        sql,
        format: 'application/json',
      }),
    });
  }

  // Execute SQL and return data as array of objects
  async queryData<T>(sql: string): Promise<T[]> {
    const result = await this.query(sql);
    if (!result.rows || result.rows.length === 0) return [];
    
    // Convert rows array to objects using columns
    return result.rows.map(row => {
      const obj: Record<string, unknown> = {};
      result.columns.forEach((col, idx) => {
        obj[col] = row[idx];
      });
      return obj as T;
    });
  }

  // Escape a CSV field value properly (handles quotes, commas, newlines)
  private escapeCsvField(val: unknown): string {
    if (val === null || val === undefined) return '';
    
    const str = String(val);
    
    // If the value contains quotes, commas, or newlines, wrap in quotes and escape inner quotes
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
      // Escape double quotes by doubling them
      return `"${str.replace(/"/g, '""')}"`;
    }
    
    return str;
  }

  // Table name to ID mapping for the DFS Collections dataset
  private tableIds: Record<string, string> = {
    'Documents': 'bf874137-5ef9-957b-c538-3a1f1bd43c9d',
    'Communications': '070f6089-b8f6-8758-379c-3a1f1bd6898f',
    'Cases': '771abddb-a852-662e-1c2b-3a1f1b3a5ef9',
    'Debtors': 'e119a45d-b284-3de2-1d88-3a1f1b2c0627',
    'Payments': 'c5980080-eced-552d-eb01-3a1f1b3b87ae',
    'PaymentPlans': '8e0e4fb9-de07-5160-3079-3a1f1b2c0946',
    'ExceptionRequests': 'fba5e07f-4c8f-f599-b7fa-3a1f1bd4820d',
    'ChatMessages': 'd11d2a50-e75a-50b5-4052-3a1f18dfbf74',
    'FileContents': 'dae71873-f580-de18-4d73-3a1f1ae4dddd',
  };

  // Append data to a table
  async appendToTable(tableIdentifier: string, data: Record<string, unknown>[]): Promise<void> {
    // Convert data to CSV format
    if (data.length === 0) return;
    
    // Get the table ID from the mapping, or use the identifier if it's already a GUID
    const tableId = this.tableIds[tableIdentifier] || tableIdentifier;
    
    const columns = Object.keys(data[0]);
    const csvRows = [
      columns.join(','),
      ...data.map(row => columns.map(col => this.escapeCsvField(row[col])).join(','))
    ];
    const csvContent = csvRows.join('\n');
    
    const formData = new FormData();
    formData.append('File', new Blob([csvContent], { type: 'text/csv' }), 'data.csv');

    const response = await fetch(
      `${API_BASE}/Dataset/${this.datasetId}/table/${tableId}/AppendTable`,
      {
        method: 'PATCH',
        headers: {
          'X-API-KEY': this.apiKey,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to append data: ${error}`);
    }
  }

  // Create or get a conversation with an assistant agent
  // Returns chatId needed for subsequent messages
  // inputParameters are passed to the agent as context variables
  async createConversation(
    agentCode: string,
    userIdentifier: string,
    inputParameters?: Array<{ key: string; value: string }>
  ): Promise<ConversationResponse> {
    const body: Record<string, unknown> = { userIdentifier };
    
    if (inputParameters && inputParameters.length > 0) {
      body.inputParameters = inputParameters;
    }
    
    return this.fetch<ConversationResponse>(`/Agent/${agentCode}/conversation`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Send a message to an existing conversation
  // Can include additional context variables
  async sendMessage(
    agentCode: string,
    chatId: string,
    message: string,
    contextVariables?: Record<string, string>
  ): Promise<ExecuteResponse> {
    const params: Array<{ Key: string; Value: string }> = [
      { Key: 'chatId', Value: chatId },
      { Key: 'message', Value: message },
    ];
    
    // Add any additional context variables
    if (contextVariables) {
      Object.entries(contextVariables).forEach(([key, value]) => {
        params.push({ Key: key, Value: value });
      });
    }
    
    return this.fetch<ExecuteResponse>(`/agent/${agentCode}/execute`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // Chat with an assistant agent (convenience method)
  // Creates conversation if no chatId provided, then sends message
  // contextParams are passed to the agent for dataset queries (e.g., debtorId, phone)
  async chatWithAgent(
    agentCode: string,
    message: string,
    chatId?: string,
    userIdentifier?: string,
    contextParams?: Record<string, string>
  ): Promise<AgentResponse> {
    // If no chatId, create a new conversation first with context parameters
    let effectiveChatId = chatId;
    if (!effectiveChatId) {
      // Convert context params to inputParameters format
      const inputParameters = contextParams 
        ? Object.entries(contextParams).map(([key, value]) => ({ key, value }))
        : undefined;
      
      const conv = await this.createConversation(
        agentCode, 
        userIdentifier || 'anonymous',
        inputParameters
      );
      effectiveChatId = conv.chatId;
    }

    // Send the message with context variables
    const result = await this.sendMessage(agentCode, effectiveChatId, message, contextParams);
    
    return {
      response: result.content,
      sessionId: effectiveChatId,
      metadata: {
        instanceId: result.instanceId,
        userMessageId: result.userMessageId,
        agentMessageId: result.agentMessageId,
      },
    };
  }

  // Execute an activity agent
  async executeAgent(
    agentCode: string,
    variables: Record<string, string>
  ): Promise<AgentResponse> {
    const params = Object.entries(variables).map(([key, value]) => ({
      Key: key,
      Value: value,
    }));
    const result = await this.fetch<ExecuteResponse>(`/agent/${agentCode}/execute`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    
    return {
      response: result.content,
      metadata: {
        jsonContent: result.jsonContent,
        instanceId: result.instanceId,
      },
    };
  }

  // Upload a file to Volatile Knowledge
  async uploadVolatileKnowledge(file: File): Promise<{ id: string; publicUrl: string; status: string }> {
    const formData = new FormData();
    formData.append('File', file); // Note: capital 'F' is required by API

    const response = await fetch(`${API_BASE}/VolatileKnowledge`, {
      method: 'POST',
      headers: {
        'X-API-KEY': this.apiKey,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to upload file: ${error}`);
    }

    const result = await response.json();
    return {
      id: result.id,
      publicUrl: `${config.serenityBaseUrl}/api/v2/file/download/public/${result.id}`,
      status: result.status,
    };
  }

  // Wait for Volatile Knowledge processing to complete
  async waitForVolatileKnowledge(vkId: string, maxWaitMs = 30000): Promise<{ status: string }> {
    const startTime = Date.now();
    while (Date.now() - startTime < maxWaitMs) {
      const response = await fetch(`${API_BASE}/VolatileKnowledge/${vkId}`, {
        method: 'GET',
        headers: {
          'X-API-KEY': this.apiKey,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to check VK status');
      }
      
      const details = await response.json();
      if (details.status === 'success') {
        return { status: 'success' };
      }
      if (details.status === 'error') {
        throw new Error('Volatile Knowledge processing failed');
      }
      
      // Still analyzing, wait 500ms
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    throw new Error('Volatile Knowledge processing timeout');
  }

  // Chat method alias for compatibility
  // contextParams: { debtorId, phone, etc. } passed to agent for automatic identification
  async chat(
    agentCode: string,
    message: string,
    chatId?: string,
    userIdentifier?: string,
    contextParams?: Record<string, string>
  ): Promise<AgentResponse> {
    return this.chatWithAgent(agentCode, message, chatId, userIdentifier, contextParams);
  }

  // Insert data into a table (simplified for demo)
  async insertData(tableName: string, data: Record<string, unknown>): Promise<void> {
    await this.appendToTable(tableName, [data]);
  }

  // Update data in a table using SQL
  async updateData(tableName: string, data: Record<string, unknown>, whereClause: string): Promise<void> {
    // Build SET clause from data
    const setClauses = Object.entries(data)
      .map(([key, value]) => {
        if (value === null || value === undefined) return `${key} = NULL`;
        if (typeof value === 'string') return `${key} = '${value.replace(/'/g, "''")}'`;
        if (typeof value === 'number') return `${key} = ${value}`;
        if (typeof value === 'boolean') return `${key} = ${value}`;
        return `${key} = '${String(value).replace(/'/g, "''")}'`;
      })
      .join(', ');
    
    const sql = `UPDATE ${tableName} SET ${setClauses} WHERE ${whereClause}`;
    await this.query(sql);
  }

  // Classify a document using the analyzer agent
  async classifyDocument(
    debtorId: string,
    documentId: string,
    documentType: string,
    volatileKnowledgeId?: string
  ): Promise<{ documentType: string; confidence: number; extractedData?: unknown }> {
    const variables: Record<string, string> = {
      debtorId,
      documentId,
      documentType,
    };
    
    if (volatileKnowledgeId) {
      variables.volatileKnowledgeIds = JSON.stringify([volatileKnowledgeId]);
    }
    
    const result = await this.executeAgent('dfs-analizador', variables);
    
    // Try to parse JSON from response
    try {
      const parsed = JSON.parse(result.response);
      return {
        documentType: parsed.document_type_confirmed || documentType,
        confidence: parsed.authenticity_score || 0.8,
        extractedData: parsed.extracted_data,
      };
    } catch {
      return {
        documentType,
        confidence: 0.7,
      };
    }
  }

  // Evaluate a hardship request using the evaluator agent
  async evaluateHardship(
    requestId: string,
    debtorId: string,
    caseId: string
  ): Promise<{
    verified: boolean;
    category: string;
    severityScore: number;
    recommendedRelief: string;
    reliefTerms?: string;
    approvalRecommendation: string;
    confidenceScore: number;
  }> {
    const result = await this.executeAgent('dfs-evaluador', {
      requestId,
      debtorId,
      caseId,
    });
    
    // Try to parse JSON from response
    try {
      const parsed = JSON.parse(result.response);
      return {
        verified: parsed.hardship_verified ?? true,
        category: parsed.category || 'other',
        severityScore: parsed.severity_score || 50,
        recommendedRelief: parsed.recommended_relief || 'payment_pause',
        reliefTerms: parsed.relief_terms,
        approvalRecommendation: parsed.approval_recommendation || 'pending_review',
        confidenceScore: parsed.confidence_score || 0.7,
      };
    } catch {
      return {
        verified: true,
        category: 'other',
        severityScore: 50,
        recommendedRelief: 'payment_pause',
        approvalRecommendation: 'pending_review',
        confidenceScore: 0.7,
      };
    }
  }
}

export const serenityClient = new SerenityClient();
